import AuthenticationResponse from "../api/responses/AuthenticationResponse";
import { localStorageKeys } from "./localStorageKeys";

export interface CurrentCity {
  cityId: string;
  cityName: string;
}

const hasValue = (value?: string): value is string =>
  typeof value === "string" && value.trim().length > 0;

const parseCurrentCity = (raw: string | null): CurrentCity | undefined => {
  if (!raw) {
    return undefined;
  }

  try {
    const parsed = JSON.parse(raw) as Partial<CurrentCity>;
    if (hasValue(parsed.cityId) && hasValue(parsed.cityName)) {
      return {
        cityId: parsed.cityId,
        cityName: parsed.cityName,
      };
    }
  } catch {
    return undefined;
  }

  return undefined;
};

export const getStoredCurrentCity = (): CurrentCity | undefined =>
  parseCurrentCity(localStorage.getItem(localStorageKeys.currentcity));

export const setStoredCurrentCity = (city: CurrentCity): void => {
  localStorage.setItem(localStorageKeys.currentcity, JSON.stringify(city));
};

export const clearStoredCurrentCity = (): void => {
  localStorage.removeItem(localStorageKeys.currentcity);
};

export const getCurrentCity = (
  fallbackProfile?: Pick<AuthenticationResponse, "cityId" | "cityName">
): CurrentCity | undefined => {
  const storedCity = getStoredCurrentCity();
  if (storedCity) {
    return storedCity;
  }

  const profileCityId = fallbackProfile?.cityId;
  const profileCityName = fallbackProfile?.cityName;

  if (hasValue(profileCityId) && hasValue(profileCityName)) {
    return {
      cityId: profileCityId,
      cityName: profileCityName,
    };
  }

  return undefined;
};

export const initializeCurrentCityFromProfile = (
  profile?: Pick<AuthenticationResponse, "cityId" | "cityName">
): CurrentCity | undefined => {
  const currentCity = getCurrentCity(profile);
  if (currentCity && !getStoredCurrentCity()) {
    setStoredCurrentCity(currentCity);
  }

  return currentCity;
};
